import { useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import type { TypographyProps } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export type CopyableTextProps = {
  text: string | number;
  label?: string;
  typographyProps?: TypographyProps;
  showIcon?: boolean;
  maxLength?: number;
};

export const CopyableText = ({
  text,
  label,
  typographyProps = {},
  showIcon = true,
  maxLength,
}: CopyableTextProps) => {
  // "idle" | "copied" | a failure message. Clipboard writes are refused
  // outright in some mobile browsers, so a failure has to say so in the
  // tooltip rather than only in a console nobody can open.
  const [status, setStatus] = useState<"idle" | "copied" | string>("idle");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(text));
      setStatus("copied");
      setTimeout(() => setStatus("idle"), 2000);
    } catch (err) {
      setStatus(
        `Couldn't copy: ${err instanceof Error ? err.message : String(err)}`,
      );
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  const copyTooltip =
    status === "idle"
      ? "Copy to clipboard"
      : status === "copied"
        ? "Copied!"
        : status;

  const textString = String(text);
  const shouldTruncate =
    maxLength !== undefined && textString.length > maxLength;
  const displayText = shouldTruncate
    ? `${textString.substring(0, maxLength)}...`
    : textString;

  const textElement = (
    <Typography variant="body2" {...typographyProps}>
      {displayText}
    </Typography>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {label && (
        <Typography
          variant="body2"
          {...typographyProps}
          sx={[
            {
              color: "text.secondary",
            },
            ...(Array.isArray(typographyProps.sx)
              ? typographyProps.sx
              : [typographyProps.sx]),
          ]}
        >
          {label}:
        </Typography>
      )}
      {shouldTruncate ? (
        <Tooltip title={textString} arrow>
          {textElement}
        </Tooltip>
      ) : (
        textElement
      )}
      {showIcon && (
        <Tooltip
          title={copyTooltip}
          arrow
          open={status === "idle" ? undefined : true}
        >
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ ml: 0.5, p: 0.25 }}
            aria-label="copy"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
